import React, { useEffect, useState } from "react";
import { Ban, Plus, Trash2, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { fetchExclusions, addExclusion, removeExclusion } from "@/lib/api";
import { toast } from "sonner";

const fmtMoney = (n) => Number(n).toLocaleString("en-US", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
});

export const ExclusionsPanel = ({ currentPrice, onChange }) => {
    const [items, setItems] = useState([]);
    const [min, setMin] = useState("");
    const [max, setMax] = useState("");
    const [label, setLabel] = useState("");
    const [loading, setLoading] = useState(false);

    const refresh = async () => {
        try {
            const r = await fetchExclusions();
            setItems(r?.items || []);
        } catch (_) { /* ignore */ }
    };

    useEffect(() => { refresh(); }, []);

    const onAdd = async (e) => {
        e?.preventDefault?.();
        const minN = Number(min);
        const maxN = Number(max);
        if (!isFinite(minN) || !isFinite(maxN) || minN <= 0 || maxN <= 0) {
            toast.error("Min and Max must be positive numbers");
            return;
        }
        if (maxN <= minN) {
            toast.error("Max must be greater than Min");
            return;
        }
        setLoading(true);
        try {
            const res = await addExclusion(minN, maxN, label.trim() || null);
            await refresh();
            setMin(""); setMax(""); setLabel("");
            toast.success(`Exclusion added [$${fmtMoney(minN)}, $${fmtMoney(maxN)}]`, {
                description: res?.cancelled_active_buys
                    ? `Cancelled ${res.cancelled_active_buys} active BUY(s) inside range`
                    : "No active BUYs affected.",
            });
            onChange && onChange();
        } catch (e) {
            toast.error(`Add failed: ${e?.response?.data?.detail || e.message}`);
        } finally { setLoading(false); }
    };

    const onDelete = async (id) => {
        try {
            await removeExclusion(id);
            await refresh();
            toast.success("Exclusion removed");
            onChange && onChange();
        } catch (e) {
            toast.error(`Remove failed: ${e?.response?.data?.detail || e.message}`);
        }
    };

    return (
        <div className="panel" data-testid="exclusions-panel">
            <div className="px-4 py-3 border-b border-[#1B2A3A] flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Ban size={14} color="#FFB020" />
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#A7B4C6]">
                        BUY Exclusion Ranges
                    </div>
                </div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-[#6F7D92]">
                    {items.length} active
                </div>
            </div>
            <div className="p-4 space-y-3">
                <div className="text-xs text-[#6F7D92]">
                    Define price ranges where NEW BUY orders should NOT be placed. Any active BUY
                    inside a newly-added range will be cancelled immediately. SELL orders are never
                    affected.
                </div>

                {/* Add form */}
                <form onSubmit={onAdd} className="grid grid-cols-12 gap-2 items-end"
                      data-testid="exclusions-add-form">
                    <div className="col-span-12 md:col-span-3 flex flex-col gap-1">
                        <Label className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6]">Min ($)</Label>
                        <input
                            type="number" step="1" value={min} onChange={(e) => setMin(e.target.value)}
                            placeholder={currentPrice ? `${(currentPrice - 1000).toFixed(0)}` : "59000"}
                            className="bg-[#0B0F14] border border-[#1B2A3A] rounded-md px-2 py-1.5 text-sm font-mono text-[#E6EDF6] focus:outline-none focus:border-[#FFB020]"
                            data-testid="exclusion-min-input"
                        />
                    </div>
                    <div className="col-span-12 md:col-span-3 flex flex-col gap-1">
                        <Label className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6]">Max ($)</Label>
                        <input
                            type="number" step="1" value={max} onChange={(e) => setMax(e.target.value)}
                            placeholder={currentPrice ? `${(currentPrice - 500).toFixed(0)}` : "59500"}
                            className="bg-[#0B0F14] border border-[#1B2A3A] rounded-md px-2 py-1.5 text-sm font-mono text-[#E6EDF6] focus:outline-none focus:border-[#FFB020]"
                            data-testid="exclusion-max-input"
                        />
                    </div>
                    <div className="col-span-12 md:col-span-4 flex flex-col gap-1">
                        <Label className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6]">Label (optional)</Label>
                        <input
                            type="text" maxLength={48} value={label} onChange={(e) => setLabel(e.target.value)}
                            placeholder="e.g. CPI news window"
                            className="bg-[#0B0F14] border border-[#1B2A3A] rounded-md px-2 py-1.5 text-sm text-[#E6EDF6] focus:outline-none focus:border-[#FFB020]"
                            data-testid="exclusion-label-input"
                        />
                    </div>
                    <div className="col-span-12 md:col-span-2">
                        <Button
                            type="submit" disabled={loading || !min || !max}
                            className="w-full bg-[#FFB020] hover:bg-[#FFC242] text-[#0B0F14] disabled:opacity-40 gap-1.5"
                            data-testid="exclusion-add-button"
                        >
                            <Plus size={14} /> Add
                        </Button>
                    </div>
                </form>

                {/* List */}
                <div className="space-y-1.5 max-h-[220px] overflow-y-auto log-scroll">
                    {items.length === 0 && (
                        <div className="text-[11px] text-[#3A4759] py-3 text-center">
                            No exclusions — all $250 grid levels below market will receive BUY orders.
                        </div>
                    )}
                    {items.map((it) => {
                        const inRange = currentPrice && currentPrice >= it.min && currentPrice <= it.max;
                        return (
                            <div
                                key={it.id}
                                className="flex items-center gap-3 py-2 px-3 rounded-md"
                                style={{
                                    background: "rgba(255,176,32,0.06)",
                                    border: "1px solid rgba(255,176,32,0.18)",
                                }}
                                data-testid={`exclusion-row-${it.id}`}
                            >
                                <div className="font-mono text-sm text-[#FFB020]">
                                    ${fmtMoney(it.min)} — ${fmtMoney(it.max)}
                                </div>
                                {it.label && (
                                    <div className="flex items-center gap-1 text-[11px] text-[#A7B4C6]">
                                        <Pencil size={10} /> {it.label}
                                    </div>
                                )}
                                {inRange && (
                                    <span
                                        className="text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded"
                                        style={{
                                            color: "#FF5C6C", background: "rgba(255,92,108,0.10)",
                                            border: "1px solid rgba(255,92,108,0.25)",
                                        }}
                                    >
                                        price in range
                                    </span>
                                )}
                                <div className="flex-1" />
                                <button
                                    onClick={() => onDelete(it.id)}
                                    className="btn-anim text-[#FF5C6C] hover:text-[#FF7080] p-1.5 rounded-md hover:bg-[#13202D]"
                                    title="Delete"
                                    data-testid={`exclusion-delete-${it.id}`}
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default ExclusionsPanel;
